import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, Calendar, MessageSquare, Clock, CheckCircle2, 
  Bot, Trash2, X, Search, ListChecks, AlertOctagon,
  FileText, ChevronRight, Loader2, Download, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { projectAPI } from '../lib/api';
import MarkdownRenderer from '../components/MarkdownRenderer';

export default function MeetingsPanel({ projectId, canEdit = true }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [creatingTasks, setCreatingTasks] = useState(false);
  const [creatingShowstoppers, setCreatingShowstoppers] = useState(false);
  
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    attendees: '',
  });

  useEffect(() => {
    loadMeetings();
  }, [projectId]);

  const loadMeetings = async () => {
    try {
      const res = await projectAPI.getMeetings(projectId);
      setMeetings(res.data || []);
    } catch (err) {
      console.log('Loading meetings:', err);
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMeeting = async () => {
    if (!newMeeting.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      const data = {
        ...newMeeting,
        attendees: newMeeting.attendees.split(',').map(a => a.trim()).filter(Boolean),
      };
      await projectAPI.addMeeting(projectId, data);
      setShowAddModal(false);
      setNewMeeting({ title: '', date: new Date().toISOString().split('T')[0], notes: '', attendees: '' });
      await loadMeetings();
      toast.success('Meeting added');
    } catch (err) {
      toast.error('Failed to add meeting');
    }
  };

  const handleDelete = async (meetingId) => {
    try {
      await projectAPI.deleteMeeting(projectId, meetingId);
      setSelectedMeeting(null);
      setAnalysis(null);
      await loadMeetings();
      toast.success('Deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleUpdateNotes = async (notes) => {
    if (!selectedMeeting) return;
    try {
      await projectAPI.updateMeeting(projectId, selectedMeeting.id, { notes });
      setSelectedMeeting(prev => ({ ...prev, notes }));
      // Update in list too
      setMeetings(prev => prev.map(m => m.id === selectedMeeting.id ? { ...m, notes } : m));
    } catch (err) {
      console.log('Auto-save error:', err);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedMeeting?.notes?.trim()) {
      toast.error('Please add meeting notes first');
      return;
    }
    setAnalyzing(true);
    try {
      const res = await projectAPI.analyzeMeeting(projectId, { notes: selectedMeeting.notes });
      setAnalysis(res.data.analysis);
      
      // Save analysis to meeting
      await projectAPI.updateMeeting(projectId, selectedMeeting.id, {
        summary: res.data.analysis.summary,
        action_items: res.data.analysis.action_items,
        showstoppers_found: res.data.analysis.showstoppers,
        status: 'analyzed'
      });
      
      setSelectedMeeting(prev => ({
        ...prev,
        summary: res.data.analysis.summary,
        action_items: res.data.analysis.action_items,
        showstoppers_found: res.data.analysis.showstoppers,
        status: 'analyzed'
      }));
      
      await loadMeetings();
      toast.success(`Analysis complete! Found ${res.data.analysis.showstoppers?.length || 0} showstoppers, ${res.data.analysis.action_items?.length || 0} action items`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to analyze meeting');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCreateTasks = async () => {
    if (!analysis?.action_items?.length && !selectedMeeting?.action_items?.length) {
      toast.error('No action items to create tasks from');
      return;
    }
    setCreatingTasks(true);
    try {
      const items = analysis?.action_items || selectedMeeting?.action_items || [];
      const res = await projectAPI.createTasksFromMeeting(projectId, selectedMeeting.id, { action_items: items });
      toast.success(`Created ${res.data.tasks_created} tasks!`);
    } catch (err) {
      toast.error('Failed to create tasks');
    } finally {
      setCreatingTasks(false);
    }
  };

  const handleCreateShowstoppers = async () => {
    if (!analysis?.showstoppers?.length && !selectedMeeting?.showstoppers_found?.length) {
      toast.error('No showstoppers to add');
      return;
    }
    setCreatingShowstoppers(true);
    try {
      const items = analysis?.showstoppers || selectedMeeting?.showstoppers_found || [];
      const res = await projectAPI.createShowstoppersFromMeeting(projectId, selectedMeeting.id, { showstoppers: items });
      toast.success(`Added ${res.data.showstoppers_created} showstoppers!`);
    } catch (err) {
      toast.error('Failed to add showstoppers');
    } finally {
      setCreatingShowstoppers(false);
    }
  };

  const filteredMeetings = meetings.filter(m => 
    m.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Get current analysis (either from state or selected meeting)
  const currentAnalysis = analysis || (selectedMeeting?.summary ? {
    summary: selectedMeeting.summary,
    action_items: selectedMeeting.action_items || [],
    showstoppers: selectedMeeting.showstoppers_found || [],
    decisions_made: selectedMeeting.decisions_made || [],
    follow_ups: selectedMeeting.follow_ups || []
  } : null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="meetings-loading">
        <div className="w-6 h-6 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex" data-testid="meetings-panel">
      {/* List */}
      <div className={`${selectedMeeting ? 'w-1/3 border-r border-[var(--surface-border)]' : 'w-full'} flex flex-col`}>
        {/* Header */}
        <div className="p-4 border-b border-[var(--surface-border)]">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-left">Meetings</h2>
                <Badge className="bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)] border-[var(--accent-indigo)]/20">
                  {meetings.length}
                </Badge>
              </div>
              <p className="text-xs opacity-60 text-left">Daily standups & meeting notes</p>
            </div>
            {canEdit && (
              <Button size="sm" onClick={() => setShowAddModal(true)} data-testid="add-meeting-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New Meeting
              </Button>
            )}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-40" />
            <Input
              placeholder="Search meetings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-sm"
              data-testid="search-meetings"
            />
          </div>
        </div>

        {/* List */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-3">
            {filteredMeetings.length === 0 ? (
              <div className="text-center py-12 opacity-60">
                <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No meetings yet</p>
                <p className="text-xs mt-1">Add your first meeting to get started</p>
              </div>
            ) : (
              filteredMeetings.map((meeting) => (
                <div
                  key={meeting.id}
                  onClick={() => { setSelectedMeeting(meeting); setAnalysis(null); }}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedMeeting?.id === meeting.id
                      ? 'border-[var(--accent-indigo)] bg-[var(--accent-indigo)]/5'
                      : 'border-[var(--surface-border)] bg-[var(--surface-card)] hover:border-[var(--accent-indigo)]/30'
                  }`}
                  data-testid={`meeting-item-${meeting.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 opacity-50" />
                      <span className="text-xs opacity-60">{formatDate(meeting.date)}</span>
                    </div>
                    {meeting.status === 'analyzed' && (
                      <Badge className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Analyzed
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-medium text-left mb-1">{meeting.title}</h3>
                  {meeting.summary && (
                    <p className="text-xs opacity-60 text-left line-clamp-2">{meeting.summary}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs opacity-40">
                    {meeting.action_items?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <ListChecks className="w-3 h-3" /> {meeting.action_items.length} tasks
                      </span>
                    )}
                    {meeting.showstoppers_found?.length > 0 && (
                      <span className="flex items-center gap-1 text-amber-400">
                        <AlertOctagon className="w-3 h-3" /> {meeting.showstoppers_found.length} blockers
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Detail Panel */}
      {selectedMeeting && (
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-[var(--surface-border)]">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-[var(--accent-indigo)]" />
                  <span className="text-sm opacity-60">{formatDate(selectedMeeting.date)}</span>
                </div>
                <h3 className="text-lg font-semibold text-left">{selectedMeeting.title}</h3>
                {selectedMeeting.attendees?.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-1 text-xs opacity-50">
                    <Users className="w-3 h-3" />
                    <span>{selectedMeeting.attendees.join(', ')}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-1">
                {canEdit && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => handleDelete(selectedMeeting.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setSelectedMeeting(null); setAnalysis(null); }}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-4">
              {/* Meeting Notes Input */}
              <div>
                <label className="text-xs font-medium mb-2 block text-left flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" />
                  Meeting Notes / Transcription
                </label>
                <Textarea
                  value={selectedMeeting.notes || ''}
                  onChange={(e) => {
                    setSelectedMeeting(prev => ({ ...prev, notes: e.target.value }));
                    // Debounced auto-save
                    clearTimeout(window.meetingNotesTimeout);
                    window.meetingNotesTimeout = setTimeout(() => handleUpdateNotes(e.target.value), 1000);
                  }}
                  placeholder="Paste your meeting notes or Teams/Zoom transcription here...

Example:
- John: We're blocked on the API integration, need credentials from the client
- Sarah: I'll follow up with them today
- Mike: The new dashboard feature is ready for review
- Action item: Sarah to contact client by EOD
- Blocker: Can't proceed with payment integration until credentials received"
                  className="min-h-[200px] text-sm bg-transparent border-[var(--surface-border)]"
                  disabled={!canEdit}
                  data-testid="meeting-notes-input"
                />
              </div>

              {/* Analyze Button */}
              {canEdit && (
                <Button 
                  onClick={handleAnalyze} 
                  disabled={analyzing || !selectedMeeting.notes?.trim()}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white"
                  data-testid="analyze-meeting-btn"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing meeting notes...
                    </>
                  ) : (
                    <>
                      <Bot className="w-4 h-4 mr-2" />
                      Analyze with AI
                    </>
                  )}
                </Button>
              )}

              {/* Analysis Results */}
              {currentAnalysis && (
                <div className="space-y-4 pt-4 border-t border-[var(--surface-border)]">
                  {/* Summary */}
                  {currentAnalysis.summary && (
                    <div className="bg-[var(--surface-card)] rounded-lg p-4 border border-[var(--surface-border)]">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2 text-left">
                        <FileText className="w-4 h-4 text-[var(--accent-indigo)]" />
                        Meeting Summary
                      </h4>
                      <p className="text-sm opacity-80 text-left">{currentAnalysis.summary}</p>
                    </div>
                  )}

                  {/* Showstoppers */}
                  {currentAnalysis.showstoppers?.length > 0 && (
                    <div className="bg-amber-500/5 rounded-lg p-4 border border-amber-500/20">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium flex items-center gap-2 text-amber-400">
                          <AlertOctagon className="w-4 h-4" />
                          Showstoppers Found ({currentAnalysis.showstoppers.length})
                        </h4>
                        {canEdit && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-xs h-7 border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                            onClick={handleCreateShowstoppers}
                            disabled={creatingShowstoppers}
                            data-testid="create-showstoppers-btn"
                          >
                            {creatingShowstoppers ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                            Add to Showstoppers
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {currentAnalysis.showstoppers.map((ss, i) => (
                          <div key={i} className="bg-[var(--surface-card)] rounded-md p-3 border border-[var(--surface-border)]">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className={`text-[10px] ${
                                ss.severity === 'Critical' ? 'text-red-400 border-red-500/30' :
                                ss.severity === 'High' ? 'text-amber-400 border-amber-500/30' : ''
                              }`}>
                                {ss.severity}
                              </Badge>
                              {ss.mentioned_by && (
                                <span className="text-xs opacity-50">by {ss.mentioned_by}</span>
                              )}
                            </div>
                            <h5 className="text-sm font-medium text-left">{ss.title}</h5>
                            {ss.description && (
                              <p className="text-xs opacity-60 text-left mt-1">{ss.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Items */}
                  {currentAnalysis.action_items?.length > 0 && (
                    <div className="bg-emerald-500/5 rounded-lg p-4 border border-emerald-500/20">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium flex items-center gap-2 text-emerald-400">
                          <ListChecks className="w-4 h-4" />
                          Action Items ({currentAnalysis.action_items.length})
                        </h4>
                        {canEdit && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-xs h-7 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                            onClick={handleCreateTasks}
                            disabled={creatingTasks}
                            data-testid="create-tasks-btn"
                          >
                            {creatingTasks ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                            Create Tasks
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {currentAnalysis.action_items.map((item, i) => (
                          <div key={i} className="bg-[var(--surface-card)] rounded-md p-3 border border-[var(--surface-border)] flex items-start gap-3">
                            <div className="w-5 h-5 rounded bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                              <span className="text-xs font-bold text-emerald-400">{i + 1}</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-left">{item.task}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {item.assignee && item.assignee !== 'Unassigned' && (
                                  <Badge variant="outline" className="text-[10px]">
                                    <Users className="w-2.5 h-2.5 mr-1" />
                                    {item.assignee}
                                  </Badge>
                                )}
                                {item.priority && (
                                  <Badge className={`text-[10px] ${
                                    item.priority === 'high' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                                    item.priority === 'medium' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                                    'bg-gray-500/10 text-gray-400 border-gray-500/20'
                                  }`}>
                                    {item.priority}
                                  </Badge>
                                )}
                                {item.due_date && (
                                  <span className="text-xs opacity-50">Due: {item.due_date}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Decisions Made */}
                  {currentAnalysis.decisions_made?.length > 0 && (
                    <div className="bg-[var(--surface-card)] rounded-lg p-4 border border-[var(--surface-border)]">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2 text-left">
                        <CheckCircle2 className="w-4 h-4 text-[var(--accent-indigo)]" />
                        Decisions Made
                      </h4>
                      <ul className="space-y-1">
                        {currentAnalysis.decisions_made.map((decision, i) => (
                          <li key={i} className="text-sm opacity-80 text-left flex items-start gap-2">
                            <ChevronRight className="w-3 h-3 mt-1 shrink-0 text-[var(--accent-indigo)]" />
                            {decision}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Follow-ups */}
                  {currentAnalysis.follow_ups?.length > 0 && (
                    <div className="bg-[var(--surface-card)] rounded-lg p-4 border border-[var(--surface-border)]">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2 text-left">
                        <Clock className="w-4 h-4 text-[var(--accent-indigo)]" />
                        Follow-ups for Next Meeting
                      </h4>
                      <ul className="space-y-1">
                        {currentAnalysis.follow_ups.map((followUp, i) => (
                          <li key={i} className="text-sm opacity-80 text-left flex items-start gap-2">
                            <ChevronRight className="w-3 h-3 mt-1 shrink-0 text-[var(--accent-indigo)]" />
                            {followUp}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
          <div 
            className="bg-[var(--surface-card)] rounded-xl border border-[var(--surface-border)] w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="add-meeting-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">New Meeting</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Meeting Title *</label>
                <Input 
                  value={newMeeting.title} 
                  onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })} 
                  placeholder="e.g., Daily Standup, Sprint Planning"
                  data-testid="new-meeting-title"
                />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Date</label>
                <Input 
                  type="date"
                  value={newMeeting.date} 
                  onChange={(e) => setNewMeeting({ ...newMeeting, date: e.target.value })}
                  data-testid="new-meeting-date"
                />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Attendees (comma-separated)</label>
                <Input 
                  value={newMeeting.attendees} 
                  onChange={(e) => setNewMeeting({ ...newMeeting, attendees: e.target.value })} 
                  placeholder="John, Sarah, Mike"
                  data-testid="new-meeting-attendees"
                />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Initial Notes (optional)</label>
                <Textarea 
                  value={newMeeting.notes} 
                  onChange={(e) => setNewMeeting({ ...newMeeting, notes: e.target.value })} 
                  placeholder="Paste meeting notes or transcription here..."
                  rows={4}
                  data-testid="new-meeting-notes"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={handleAddMeeting} data-testid="save-meeting-btn">Create Meeting</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
